import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { getCountryCallingCode } from 'react-phone-number-input';
import { cn } from '@/lib/utils';

export function CountrySelect({
  value,
  onChange,
  options,
  iconComponent: Icon,
  disabled,
  readOnly,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const countryOptions = options.filter((opt) => opt.value);

  const filteredOptions = countryOptions.filter((opt) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      opt.label.toLowerCase().includes(q) ||
      opt.value.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      if (inputRef.current) inputRef.current.focus();
    } else {
      setSearch('');
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [search]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const activeItem = listRef.current.querySelector(
      `[data-index="${activeIndex}"]`
    );
    if (activeItem) {
      activeItem.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, open]);

  function handleOpenToggle() {
    if (!open) {
      const idx = countryOptions.findIndex((opt) => opt.value === value);
      setActiveIndex(idx >= 0 ? idx : 0);
    }
    setOpen((o) => !o);
  }

  function handleSelect(countryCode) {
    onChange(countryCode);
    setOpen(false);
  }

  function handleKeyDown(event) {
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      return;
    }
    if (!open) return;

    const len = filteredOptions.length;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (len === 0 ? 0 : (i + 1) % len));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (len === 0 ? 0 : (i - 1 + len) % len));
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(Math.max(0, len - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const opt = filteredOptions[activeIndex];
      if (opt) handleSelect(opt.value);
    }
  }

  return (
    <div
      ref={containerRef}
      className="relative flex items-stretch border-r border-slate-200"
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        disabled={disabled || readOnly}
        onClick={handleOpenToggle}
        aria-label="Select country"
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'flex h-full items-center gap-1.5 rounded-l-md px-3 transition',
          'cursor-pointer hover:bg-slate-50',
          'focus:outline-none focus-visible:bg-slate-50',
          (disabled || readOnly) && 'cursor-not-allowed opacity-50'
        )}
      >
        <span className="flex h-[18px] w-6 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-slate-100 ring-1 ring-black/10">
          {value && <Icon country={value} label={value} />}
        </span>
        <ChevronDown
          className={cn(
            'size-3 text-slate-500 transition-transform',
            open && 'rotate-180'
          )}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-50 mt-1.5 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
          role="dialog"
        >
          <div className="border-b border-slate-200 p-2">
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                aria-label="Search country"
                className="w-full rounded-md border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none transition focus:border-brand-400 focus:bg-white focus:ring-2 focus:ring-brand-500/20"
              />
            </div>
          </div>

          <ul
            ref={listRef}
            className="max-h-[280px] overflow-y-auto py-1"
            role="listbox"
            aria-label="Countries"
          >
            {filteredOptions.length === 0 ? (
              <li className="px-4 py-3 text-center text-sm text-slate-500">
                No country found
              </li>
            ) : (
              filteredOptions.map((opt, index) => {
                const callingCode = (() => {
                  try {
                    return getCountryCallingCode(opt.value);
                  } catch {
                    return null;
                  }
                })();
                const isSelected = value === opt.value;
                const isActive = index === activeIndex;
                return (
                  <li
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    data-index={index}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(opt.value)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={cn(
                        'flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left transition hover:bg-slate-50',
                        isSelected && !isActive && 'bg-brand-50 hover:bg-brand-50',
                        isActive && 'bg-brand-100 hover:bg-brand-100'
                      )}
                    >
                      <span className="flex h-[18px] w-6 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-slate-100 ring-1 ring-black/10">
                        <Icon country={opt.value} label={opt.value} />
                      </span>
                      <span className="flex-1 truncate text-sm text-slate-900">
                        {opt.label}
                      </span>
                      {callingCode && (
                        <span className="ml-auto shrink-0 text-sm text-slate-500">
                          +{callingCode}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}